import type {
  AiMotionGoalType,
  AiMotionModel,
  AiMotionInputConfig as BaseAiMotionInputConfig,
} from '@dvb/common';
import type { DetectAccuracy } from '@school-manage/common/enums';

import type { Course } from '../courses/courses.types';

export enum CourseTaskStatus {
  COMPLETED = 'COMPLETED',
  SUBMITTING = 'SUBMITTING',
  UPCOMING = 'UPCOMING',
}

export enum SubmitRecordStatus {
  FAILURE = 'FAILURE',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  PENDING = 'PENDING',
  PUBLISH = 'PUBLISH',
  QUALIFIED = 'QUALIFIED',
}

export enum CourseTaskType {
  AI = 'AI',
  MANUAL = 'MANUAL',
}

export enum ManualType {
  CHECKBOX = 'checkbox',
  CONFIRM_CHECKBOX = 'confirm_checkbox',
  DATE = 'date',
  EMAIL = 'email',
  FILE = 'file',
  IMAGE = 'image',
  NUMBER = 'number',
  PHONE = 'phone',
  RADIO = 'radio',
  SELECT = 'select',
  TEXT = 'text',
  TEXTAREA = 'textarea',
  VIDEO = 'video',
}

export enum CourseTaskCycle {
  DAY = 'DAY',
  MONTH = 'MONTH',
  WEEK = 'WEEK',
  YEAR = 'YEAR',
}

export interface AIRecord {
  completed_at: number;
  count: number;
  goal_type?: AiMotionGoalType;
  model: AiMotionModel;
  motion_id: string;
  time: number;
  video?: string;
}

export type FormInputRecord = Record<
  string,
  boolean | number | string | string[]
>;

export type FormInputRecordWithFile = Record<
  string,
  FileList | FormInputRecord[string]
>;

interface BaseInputConfigData {
  help_text?: string;
  label: string;
  multiple?: boolean;
  name: string;
  required: boolean;
}

export interface BaseInputConfig {
  data: BaseInputConfigData;
  type: Exclude<
    ManualType,
    | ManualType.CHECKBOX
    | ManualType.CONFIRM_CHECKBOX
    | ManualType.RADIO
    | ManualType.SELECT
  >;
}

export interface SelectInputConfig {
  data: {
    options?: Record<string, string>;
  } & BaseInputConfigData;
  type: ManualType.SELECT;
}

export interface RadioInputConfig {
  data: {
    options?: string[];
  } & BaseInputConfigData;
  type: ManualType.RADIO;
}

export interface CheckboxInputConfig {
  data: {
    max_selections?: number;
    min_selections?: number;
    options?: Record<string, string>;
  } & BaseInputConfigData;
  type: ManualType.CHECKBOX;
}

export interface ConfirmCheckboxInputConfig {
  data: BaseInputConfigData;
  type: ManualType.CONFIRM_CHECKBOX;
}

export type InputConfig =
  | BaseInputConfig
  | CheckboxInputConfig
  | ConfirmCheckboxInputConfig
  | RadioInputConfig
  | SelectInputConfig;

export interface AiMotionInputConfig extends BaseAiMotionInputConfig {
  detect_accuracy: DetectAccuracy;
}

export interface CourseTaskConfig {
  ai_motion: AiMotionInputConfig[];
  all_submit: number; // 所有人已提交次数
  cycle: '' | CourseTaskCycle;
  is_advance: boolean;
  is_cycle: boolean;
  manual?: InputConfig[];
  max: number; // 所有人提交次数上限
  tx_max: number; // 每人提交次数上限
}

export interface CourseTask {
  available_submit: number; // 剩余提交次数
  config: CourseTaskConfig;
  course: Course;
  created_at: string;
  description: string;
  end_at: null | number;
  name: string;
  remarks?: null | string;
  start_at: number;
  total_submit: number; // 总提交次数
  type: CourseTaskType;
  uuid: string;
}

interface CourseTaskSubmitRecordDetails {
  ai_motion: AIRecord[];
  manual_submit: FormInputRecord;
}

interface TeacherScoreRecord {
  comments: string;
  created_at: number;
  score: string;
  status: SubmitRecordStatus;
  teacher: string;
  uuid: string;
}

export interface CourseTaskSubmitRecord {
  course: Course;
  course_task: CourseTask;
  created_at: string;
  details: CourseTaskSubmitRecordDetails;
  remarks: string;
  status: SubmitRecordStatus;
  teacher_score_record?: TeacherScoreRecord[];
  uuid: string;
}

export interface GetTasksArgs {
  courseUuid?: string;
  cursor?: string;
  search?: string;
  status?: '' | CourseTaskStatus; // '' for all tasks
}

export interface GetTaskSubmitRecordsArgs {
  courseTaskUuid: string;
  courseUuid?: string;
  cursor?: string;
}

export interface SubmitTaskArgs {
  aiRecord: AIRecord[];
  formData: FormInputRecord;
  taskUuid: string;
}

export interface UploadTempFileRes {
  id: string;
}
